import { useState, useEffect } from 'react';
import ListColumn from 'src/componentes/NewList/ListColumn';
import { useIntl } from 'react-intl';
import useAuth from 'src/hooks/useAuth';
import { capitalizeFirstLetter } from 'src/utils/string';
import ContentManager from 'src/componentes/content-manager';
import NewListTree from 'src/componentes/NewList/NewListTree';
import { ErrorBoundary } from 'react-error-boundary';
import { COLUNAS, FILTROS } from 'src/utils/constants';
import RiscoEditView from 'src/paginas/gestao-risco/risco/RiscoEditView';
import ScoreAcumulado from 'src/componentes/score-acumulado/index';
import { getConfiguration } from 'src/services/commonRequests';
import LucideIcon from 'src/componentes/lucide-icon';
import Button from 'src/componentes/button';
import Badge from 'src/componentes/Badge';
import StatusBadge from 'src/componentes/StatusBadge';
import SearchComponent from './RiscoCategorizadoSearch';
import SearchSchema from './SearchSchema';

function DicionarioRiscoView({
  columnsTag = COLUNAS.DICIONARIO_RISCOS,
  filterTag = FILTROS.RISCO_CATEGORIZADO,
  entityTag = 6,
  defaultSort,
  defaultSortBy,
  parameters = {},
  endpoint = '/RiscoCategorizado',
  controleId,
  disabled,
  select = false,
  afterSave,
  calcularProbabilidadeGeralPorTipo,
  selectedFilter: selectedFilterProp = null,
  openLastFilter = true,
  showToolbar = true,
  configuracao,
  ...rest
}) {
  const intl = useIntl();
  const { terms: resources, thousandsSeparator, decimalSeparator } = useAuth();
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [saved, setSaved] = useState(1);
  const [configuracaoProcesso, setConfiguracaoProcesso] = useState(null);

  useEffect(() => {
    getConfiguration('ItemConfiguracaoProcesso')((configuracaoProcesso) =>
      setConfiguracaoProcesso(configuracaoProcesso)
    )();
  }, []);

  const handleRefresh = (filter) => {
    setSaved((prevSaved) => prevSaved + 1);
    setSelectedFilter(filter);
  };

  const closeModal = (saved) => {
    if (saved) {
      handleRefresh();
    }
  };

  const handleNomeClick = (item) => {
    ContentManager.addContent(
      <RiscoEditView
        mode="edit"
        useHandleClose={true}
        handleClose={(saved) => closeModal(saved)}
        item={item}
        {...rest}
      />
    );
  };

  const renderNome = (item) => {
    return item.isRisk ? (
      <a
        tabIndex="-1"
        onClick={() => handleNomeClick(item)}
        style={{ cursor: 'pointer' }}
      >
        {item.nome}
      </a>
    ) : (
      <p>
        <LucideIcon className="mr-2" icon={'folder-outline'} />
        {item.nome}
      </p>
    );
  };

  const renderScoreAcumulado = (
    { id, scoreAcumuladoDoGrau, isRisk },
    exibirPontuacaoColunaScoreRisco
  ) => {
    return (
      <ScoreAcumulado
        id={id}
        customId={
          isRisk
            ? 'scoreAcumuladoDoGrauRisk-' + id
            : 'scoreAcumuladoDoGrau-' + id
        }
        //showPopOver={!isRisk}
        scoreAcumuladoDoGrau={scoreAcumuladoDoGrau}
        showValue={exibirPontuacaoColunaScoreRisco}
        thousandsSeparator={thousandsSeparator}
        decimalSeparator={decimalSeparator}
      />
    );
  };

  const renderMaiorGrauRisco = ({ maiorGrauRisco }) => {
    return (
      <div className="d-flex w-100 justify-content-center">
        <div
          className="pl-2 pr-2 rounded-lg"
          style={{
            backgroundColor: maiorGrauRisco?.cor,
            color: 'white',
            whiteSpace: 'nowrap',
          }}
        >
          {maiorGrauRisco?.descricao}
        </div>
      </div>
    );
  };

  const renderNivel = (item) => {
    return <p>{item.nivel}</p>;
  };

  const renderTags = ({ tags }) => {
    if (!Array.isArray(tags)) return tags;

    return tags.map((tag, index) => (
      <Badge
        key={tag.id ?? tag.descricao ?? index}
        pill
        className="text-white"
        style={{ background: tag.cor }}
      >
        {tag.descricao}
      </Badge>
    ));
  };

  const renderStatus = (item) => {
    if (!item.statusRisco) return;

    return (
      <StatusBadge
        text={item.statusRisco?.descricao || '-'}
        textColor={
          item.statusRisco?.corFundo ? item.statusRisco?.cor : undefined
        }
        backgroundColor={item.statusRisco?.corFundo || item.statusRisco?.cor}
      />
    );
  };

  const renderToleranciaRisco = ({ ratingToleranciaRisco }) => {
    if (!ratingToleranciaRisco) return null;
    if (typeof ratingToleranciaRisco === 'string') return ratingToleranciaRisco;

    return (
      <Badge
        pill
        className="text-white"
        style={{ background: ratingToleranciaRisco.cor }}
      >
        {ratingToleranciaRisco.descricao}
      </Badge>
    );
  };

  const defaultColumns = [
    <ListColumn
      key="categoria"
      minWidth={250}
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'categoria' })
      )}
      valueField="nome"
      valueFunction={(item) => renderNome(item)}
      default
      required
      tree
    />,
    <ListColumn
      key="scoreAcumuladoDoGrau"
      minWidth={180}
      style={{ whiteSpace: 'nowrap' }}
      headerText={
        configuracaoProcesso?.exibirPontuacaoColunaScoreRisco
          ? intl.formatMessage(
              { id: 'scoreAcumulado' },
              { risco: resources.risco.toLowerCase() }
            )
          : intl.formatMessage(
              { id: 'grauRisco' },
              { risco: resources.risco.toLowerCase() }
            )
      }
      valueField="scoreAcumuladoDoGrau"
      valueFunction={(item) => renderScoreAcumulado(item, true)}
      default
      required
    />,
    <ListColumn
      key="maiorGrauRisco"
      default
      headerText={intl.formatMessage({ id: 'exposicao' })}
      valueField="maiorGrauRisco"
      valueFunction={renderMaiorGrauRisco}
      visible={true} //configuracaoProcesso?.calcularNivelExposicaoProcesso}
      required
    />,
    <ListColumn
      key="nivel"
      maxWidth={'10%'}
      headerText={capitalizeFirstLetter(intl.formatMessage({ id: 'nivel' }))}
      valueField="nivel"
      valueFunction={(item) => renderNivel(item)}
      // tooltipHeader={intl.formatMessage(
      //   { id: 'labelQuantidadeNiveisInfo' },
      //   { risco: resources.risco }
      // )}
      default
      required
    />,
    <ListColumn
      key="idIntegracao"
      headerText={intl.formatMessage({ id: 'codigo' })}
      valueField="idIntegracao"
    />,
    <ListColumn
      key="responsaveis"
      headerText={capitalizeFirstLetter(resources.responsaveis)}
      valueField="responsaveis"
    />,
    <ListColumn
      key="area"
      headerText={capitalizeFirstLetter(resources.area)}
      valueField="area"
    />,
    <ListColumn
      key="matrizRisco"
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'matrizRisco' }, { risco: resources.risco })
      )}
      valueField="matrizRisco"
    />,
    configuracao?.utilizarTipo && (
      <ListColumn
        key="tipoRisco"
        headerText={capitalizeFirstLetter(resources.tipoRisco)}
        valueField="tipoRisco"
      />
    ),
    configuracao?.utilizarModelo && (
      <ListColumn
        key="modeloRisco"
        headerText={capitalizeFirstLetter(intl.formatMessage({ id: 'modelo' }))}
        valueField="modeloRisco"
      />
    ),
    configuracao?.podeVisualizarTag && (
      <ListColumn
        key="tags"
        headerText={intl.formatMessage(
          { id: 'tags' },
          { tags: resources.tags }
        )}
        valueField="tags"
        valueFunction={renderTags}
      />
    ),
    configuracao?.utilizarClassificacaoRisco && (
      <ListColumn
        key="classificacaoRisco"
        headerText={intl.formatMessage({ id: 'label.classificacao' })}
        valueField="classificacaoRisco"
      />
    ),
    <ListColumn
      key="statusRisco"
      headerText={intl.formatMessage({ id: 'status' })}
      valueField="statusRisco"
      valueFunction={renderStatus}
    />,
    <ListColumn
      key="descricoesCausas"
      headerText={capitalizeFirstLetter(
        intl.formatMessage(
          { id: 'labelCausasDoRisco' },
          {
            pontosproblematicos: resources.pontosproblematicos,
            risco: resources.risco,
          }
        )
      )}
      valueField="descricoesCausas"
      minWidth={300}
    />,
    <ListColumn
      key="descricoesConsequencias"
      headerText={capitalizeFirstLetter(
        intl.formatMessage(
          { id: 'labelConsequenciasDoRisco' },
          {
            consequencias: resources.consequencias,
            risco: resources.risco,
          }
        )
      )}
      valueField="descricoesConsequencias"
      minWidth={300}
    />,
    <ListColumn
      key="descricoesPontoControle"
      headerText={capitalizeFirstLetter(resources.pontosdecontrole)}
      valueField="descricoesPontoControle"
      minWidth={300}
    />,
    <ListColumn
      key="descricoesProcessos"
      headerText={intl.formatMessage({ id: 'label.itensImpactados' })}
      valueField="descricoesProcessos"
      minWidth={300}
    />,
    configuracao?.habilitarAcompanhamentoToleranciaRisco && (
      <ListColumn
        key="toleranciaRisco"
        headerText={intl.formatMessage(
          { id: 'labelToleranciaRisco' },
          { risco: resources.risco }
        )}
        valueField="toleranciaRisco"
      />
    ),
    configuracao?.habilitarAcompanhamentoToleranciaRisco && (
      <ListColumn
        key="ratingToleranciaRisco"
        headerText={intl.formatMessage(
          { id: 'labelToleranciaRiscoFaixas' },
          { risco: resources.risco }
        )}
        valueField="ratingToleranciaRisco"
        valueFunction={renderToleranciaRisco}
      />
    ),
  ];

  function Fallback({ error, resetErrorBoundary }) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Ops! Algo deu errado 🚨</h2>
        <pre>{error.message}</pre>
        <button onClick={resetErrorBoundary}>Tentar novamente</button>
      </div>
    );
  }

  const logError = (error, info) => {
    console.error('Erro capturado:', error, info);
  };

  const customLoadChildren = (item) => {
    return item.riscos || [];
  };

  const actions = [
    <Button
      key={1}
      onClick={() => handleRefresh()}
      color="primary"
      className="ml-2"
    >
      <LucideIcon icon="refresh" className="mr-2" />
      {intl.formatMessage({ id: 'label.atualizar' })}
    </Button>,
  ];

  return (
    <ErrorBoundary
      FallbackComponent={Fallback}
      onError={logError}
      onReset={() => {}}
    >
      <NewListTree
        endpoint={endpoint}
        defaultColumns={defaultColumns}
        sortField="Nome"
        showNew={false}
        showMenu={false}
        resources={resources}
        saved={saved}
        actions={actions}
        showCountPage={false}
        select={select}
        parameters={selectedFilter || parameters}
        selectedFilter={selectedFilterProp}
        openLastFilter={openLastFilter}
        showToolbar={showToolbar}
        showScrollColumn={false}
        onRefresh={handleRefresh}
        showSearch={true}
        filterTag={filterTag}
        columnsTag={columnsTag}
        entityTag={entityTag}
        useSavedFilter={true}
        searchSchema={SearchSchema}
        searchComponent={<SearchComponent configuracao={configuracao} />}
        customLoadChildren={customLoadChildren}
        filterDrawer
        tipoItem={6}
        {...rest}
      />
    </ErrorBoundary>
  );
}

export default DicionarioRiscoView;
